<template>
  <div
    :id="`${source}_dialer`"
    :class="[
      'flex flex-col text_color proxima_nova gap-2 px-2',
      dial ? 'pb-3' : 'py-3',
    ]"
  >
    <div
      :id="`${source}_dialer_input_section`"
      :class="['pr-[14px]', dial ? 'pb-2' : 'py-2']"
    >
      <div v-if="dial" class="flex flex-row justify-between items-center">
        <vue-tel-input
          ref="inputRef"
          v-model="number"
          :defaultCountry="defaultCountryCode"
          :dropdownOptions="{
            showFlags: true,
            showDialCodeInList: true,
            showSearchBox: true,
            searchBoxPlaceholder: 'Search country..',
          }"
          mode="international"
          :maxlength="15"
          @keypress="handleKeyPress"
          inputClasses="dialer_input font-semibold text_color"
        />

        <ThemeImage
          @click="handleBackspace"
          class="size-6 cursor-pointer"
          :icon="imageNames.BACKSPACE_GREY"
          alt="backspace_icon"
        />
      </div>

      <div v-else class="flex items-center justify-center w-full">
        <a-input
          v-model:value="number"
          class="dialer_input font-semibold text_color"
          @keypress="handleKeyPress"
          :maxlength="15"
          ref="inputRef"
        />
      </div>
    </div>

    <div
      :id="`${source}_dialer_digits_section`"
      class="grid grid-cols-3 gap-3 px-2"
    >
      <div
        v-for="digit in dialerDigits"
        :key="digit"
        class="col-span-1 text-base text-semibold cursor-pointer digit_container"
        @click="addDigit(digit)"
      >
        {{ digit }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { VueTelInput } from "vue-tel-input";
import { imageNames } from "@/src/constants/imageConstants.js";
import { dialerDigits } from "@/src/constants/callConstants.js";
import {
  getCurrentConnection,
  getIncomingConnection,
} from "@/src/utils/callsUtils.js";

const store = useStore();
const defaultCountryCode = computed(() => store.getters["workspace/defaultCountryCode"]);

const props = defineProps({
  source: { type: String, required: true },
  dial: { type: Boolean, default: false },
});

const emit = defineEmits(["update:value"]);

const number = ref("");
const inputRef = ref(null);

const handleBackspace = () => {
  try {
    if (number.value?.length > 0) {
      number.value = number.value.slice(0, -1);
    }

    if (inputRef.value) {
      inputRef.value.focus();
    }
  } catch (error) {
    console.error("An error occurred while handling backspace: ", error);
  }
};

const addDigit = (digit) => {
  try {
    sendDigit(digit);

    if (number.value.length < 15) {
      number.value += digit;
    }
  } catch (error) {
    console.error("An error occurred while adding digit: ", error);
  }
};

const handleKeyPress = (e) => {
  try {
    const key = String.fromCharCode(e.keyCode);
    sendDigit(key);
    if (!/[0-9*#+]/.test(key)) {
      e.preventDefault();
    }
  } catch (error) {
    console.error("An error occurred while handling key press: ", error);
  }
};

const sendDigit = (digit) => {
  try {
    const currentConnection = getCurrentConnection();
    const incomingConnection = getIncomingConnection();

    if (incomingConnection && incomingConnection.sendDigits) {
      incomingConnection.sendDigits(digit);
    }
    if (currentConnection && currentConnection.sendDigits) {
      currentConnection.sendDigits(digit);
    }
  } catch (error) {
    console.error("An error occurred while sending digit: ", error);
  }
};

watch(number, () => {
  emit("update:value", number.value);
});

onMounted(() => {
  store.dispatch("workspace/fetchDefaultCountryCode");
  if (inputRef.value) {
    inputRef.value.focus();
  }
});
</script>

<style scoped>
.dialer_input {
  border: none;
  box-shadow: none;
  background-color: transparent;
  font-size: 24px;
  font-family: "Proxima Nova", sans-serif;
  width: fit-content;
  min-width: 30px;
  text-align: center;
}

.digit_container {
  display: flex;
  height: 44px;
  padding: 20px;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;
  line-height: 19px;
  border-radius: 56px;
  border: 1px solid var(--white-opacity-8);
}

:deep(.vti__input.vti__phone) {
  font-family: "Proxima Nova", sans-serif;
  font-size: 24px !important;
  font-style: normal;
  font-weight: 600 !important;
  line-height: normal;
}

:deep(.vti__input.vti__phone::placeholder) {
  font-size: 16px !important;
}
</style>
